import React from 'react';
import { Check } from '@phosphor-icons/react';
import { Flex, Text, cn, useToast } from '@chatsdet/ui';
import {
  useCreateTag,
  useOrgTags,
  useSetCardTags,
  type Card,
  type CardTag,
  type TagColor,
} from '../../cards/hooks/useCards';

type AccountLabelOption = {
  name: string;
  color: Extract<TagColor, 'red' | 'orange' | 'yellow' | 'green'>;
};

export const ACCOUNT_LABEL_OPTIONS: AccountLabelOption[] = [
  { name: 'Red', color: 'red' },
  { name: 'Orange', color: 'orange' },
  { name: 'Yellow', color: 'yellow' },
  { name: 'Green', color: 'green' },
];

const LABEL_STYLES: Record<TagColor, { strip: string; swatch: string; button: string }> = {
  red: {
    strip: 'bg-red-500',
    swatch: 'bg-red-500',
    button: 'border-red-200 bg-red-50 text-red-950 hover:border-red-300',
  },
  orange: {
    strip: 'bg-orange-500',
    swatch: 'bg-orange-500',
    button: 'border-orange-200 bg-orange-50 text-orange-950 hover:border-orange-300',
  },
  yellow: {
    strip: 'bg-yellow-400',
    swatch: 'bg-yellow-400',
    button: 'border-yellow-200 bg-yellow-50 text-yellow-950 hover:border-yellow-300',
  },
  green: {
    strip: 'bg-green-500',
    swatch: 'bg-green-500',
    button: 'border-green-200 bg-green-50 text-green-950 hover:border-green-300',
  },
  teal: {
    strip: 'bg-teal-500',
    swatch: 'bg-teal-500',
    button: 'border-teal-200 bg-teal-50 text-teal-950 hover:border-teal-300',
  },
  blue: {
    strip: 'bg-blue-500',
    swatch: 'bg-blue-500',
    button: 'border-blue-200 bg-blue-50 text-blue-950 hover:border-blue-300',
  },
  indigo: {
    strip: 'bg-indigo-500',
    swatch: 'bg-indigo-500',
    button: 'border-indigo-200 bg-indigo-50 text-indigo-950 hover:border-indigo-300',
  },
  purple: {
    strip: 'bg-purple-500',
    swatch: 'bg-purple-500',
    button: 'border-purple-200 bg-purple-50 text-purple-950 hover:border-purple-300',
  },
  pink: {
    strip: 'bg-pink-500',
    swatch: 'bg-pink-500',
    button: 'border-pink-200 bg-pink-50 text-pink-950 hover:border-pink-300',
  },
  gray: {
    strip: 'bg-gray-400',
    swatch: 'bg-gray-400',
    button: 'border-gray-200 bg-gray-50 text-gray-950 hover:border-gray-300',
  },
};

function normalizeTagName(name: string) {
  return name.trim().toLowerCase();
}

export function findAccountLabelTag(tags: CardTag[], option: AccountLabelOption) {
  const name = normalizeTagName(option.name);
  return tags.find((tag) => normalizeTagName(tag.name) === name);
}

export function isAccountLabelTag(tag: CardTag) {
  const name = normalizeTagName(tag.name);
  return ACCOUNT_LABEL_OPTIONS.some((option) => normalizeTagName(option.name) === name);
}

export function getNextAccountLabelTagIds(
  currentTags: CardTag[],
  option: AccountLabelOption,
  targetTag: CardTag
) {
  const nonAccountTagIds = currentTags
    .filter((tag) => !isAccountLabelTag(tag))
    .map((tag) => tag.id);

  if (findAccountLabelTag(currentTags, option)) return nonAccountTagIds;
  return [...nonAccountTagIds, targetTag.id];
}

export function AccountLabelStrip({
  tags,
  className,
  max = 6,
}: {
  tags: CardTag[];
  className?: string;
  max?: number;
}) {
  if (tags.length === 0) return null;

  return (
    <div className={cn('flex flex-wrap gap-1', className)} aria-label="Account labels">
      {tags.slice(0, max).map((tag) => (
        <span
          key={tag.id}
          aria-label={`${tag.name} label`}
          title={tag.name}
          className={cn(
            'h-2 w-10 rounded-sm',
            LABEL_STYLES[tag.color]?.strip ?? LABEL_STYLES.gray.strip
          )}
        />
      ))}
      {tags.length > max && (
        <span className="text-[10px] leading-3 text-muted-foreground">+{tags.length - max}</span>
      )}
    </div>
  );
}

export function AccountLabelsPanel({ card }: { card: Card }) {
  const { data: orgTags = [], isLoading: isLoadingTags } = useOrgTags();
  const createTag = useCreateTag();
  const setCardTags = useSetCardTags('account', card.id);
  const { toast } = useToast();

  const currentTags = card.tags ?? [];
  const isPending = isLoadingTags || createTag.isPending || setCardTags.isPending;

  const toggleLabel = async (option: AccountLabelOption) => {
    try {
      const assignedTag = findAccountLabelTag(currentTags, option);
      if (assignedTag) {
        await setCardTags.mutateAsync(
          getNextAccountLabelTagIds(currentTags, option, assignedTag)
        );
        toast.success('Labels updated');
        return;
      }

      const tag =
        findAccountLabelTag(orgTags, option) ??
        (await createTag.mutateAsync({
          name: option.name,
          color: option.color,
          description: 'Account board label',
        }));

      await setCardTags.mutateAsync(getNextAccountLabelTagIds(currentTags, option, tag));
      toast.success('Labels updated');
    } catch {
      toast.error('Failed to update labels');
    }
  };

  return (
    <Flex direction="column" gap="xs">
      <Text size="sm" weight="medium">
        Labels
      </Text>
      <div className="grid grid-cols-2 gap-2">
        {ACCOUNT_LABEL_OPTIONS.map((option) => {
          const selected = Boolean(findAccountLabelTag(currentTags, option));
          const style = LABEL_STYLES[option.color];

          return (
            <button
              key={option.color}
              type="button"
              className={cn(
                'flex h-9 items-center justify-between rounded-md border px-2 text-sm font-medium transition-colors disabled:opacity-60',
                style.button,
                selected && 'ring-2 ring-offset-1 ring-primary'
              )}
              disabled={isPending}
              aria-pressed={selected}
              onClick={() => void toggleLabel(option)}
            >
              <span className="flex min-w-0 items-center gap-2">
                <span className={cn('h-3 w-3 shrink-0 rounded-sm', style.swatch)} />
                <span className="truncate">{option.name}</span>
              </span>
              {selected && <Check size={14} weight="bold" aria-hidden />}
            </button>
          );
        })}
      </div>
    </Flex>
  );
}
